import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { ArrowLeft, Upload, X, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { fetchData } = useOutletContext() || {};
  const isEdit = !!id;

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    brand: '',
    oem_number: '',
    stock: '',
    condition: 'new',
    images: [],
    compatible_vehicles: []
  });
  const [newVehicle, setNewVehicle] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');

  useEffect(() => {
    fetchCategories();
    if (isEdit) {
      fetchProduct();
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      const product = response.data;
      setFormData({
        name: product.name,
        description: product.description,
        price: product.price.toString(),
        category_id: product.category_id,
        brand: product.brand || '',
        oem_number: product.oem_number || '',
        stock: product.stock.toString(),
        condition: product.condition,
        images: product.images || [],
        compatible_vehicles: product.compatible_vehicles || []
      });
    } catch (error) {
      toast.error('Ürün bulunamadı');
      navigate('/seller/products');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddImage = () => {
    if (newImageUrl && !formData.images.includes(newImageUrl)) {
      setFormData({ ...formData, images: [...formData.images, newImageUrl] });
      setNewImageUrl('');
    }
  };

  const handleRemoveImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index)
    });
  };

  const handleAddVehicle = () => {
    if (newVehicle && !formData.compatible_vehicles.includes(newVehicle)) {
      setFormData({
        ...formData,
        compatible_vehicles: [...formData.compatible_vehicles, newVehicle]
      });
      setNewVehicle('');
    }
  };

  const handleRemoveVehicle = (index) => {
    setFormData({
      ...formData,
      compatible_vehicles: formData.compatible_vehicles.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.description || !formData.price || !formData.category_id || !formData.stock) {
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      return;
    }

    setLoading(true);
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock)
      };

      if (isEdit) {
        await axios.put(`${API}/products/${id}`, data);
        toast.success('Ürün güncellendi');
      } else {
        await axios.post(`${API}/products`, data);
        toast.success('Ürün eklendi');
      }

      fetchData?.();
      navigate('/seller/products');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="product-form-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/seller/products')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold text-slate-900">
          {isEdit ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Ürün Bilgileri</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="name">Ürün Adı *</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="Örn: BMW N54 Motor Silindir Kapağı"
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-1"
                    data-testid="product-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Açıklama *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    placeholder="Ürün hakkında detaylı açıklama..."
                    value={formData.description}
                    onChange={handleChange}
                    className="mt-1"
                    rows={5}
                    data-testid="product-description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="brand">Marka</Label>
                    <Input
                      id="brand"
                      name="brand"
                      placeholder="Örn: Bosch, Mahle"
                      value={formData.brand}
                      onChange={handleChange}
                      className="mt-1"
                      data-testid="product-brand-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="oem_number">OEM Numarası</Label>
                    <Input
                      id="oem_number"
                      name="oem_number"
                      placeholder="Örn: 11127552281"
                      value={formData.oem_number}
                      onChange={handleChange}
                      className="mt-1"
                      data-testid="product-oem-input"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Images */}
            <Card>
              <CardHeader>
                <CardTitle>Ürün Görselleri</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-4">
                  <Input
                    placeholder="Görsel URL'si girin..."
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    data-testid="image-url-input"
                  />
                  <Button type="button" onClick={handleAddImage} variant="outline">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {formData.images.length > 0 ? (
                  <div className="grid grid-cols-4 gap-4">
                    {formData.images.map((img, idx) => (
                      <div key={idx} className="relative group">
                        <img
                          src={img}
                          alt={`Ürün ${idx + 1}`}
                          className="w-full aspect-square object-cover rounded-lg border"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(idx)}
                          className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border-2 border-dashed rounded-lg p-8 text-center text-slate-400">
                    <Upload className="h-12 w-12 mx-auto mb-2" />
                    <p>Görsel URL'si ekleyin</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Compatible Vehicles */}
            <Card>
              <CardHeader>
                <CardTitle>Uyumlu Araçlar</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-4">
                  <Input
                    placeholder="Örn: BMW 3 Serisi E90 (2005-2012)"
                    value={newVehicle}
                    onChange={(e) => setNewVehicle(e.target.value)}
                    data-testid="vehicle-input"
                  />
                  <Button type="button" onClick={handleAddVehicle} variant="outline">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {formData.compatible_vehicles.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.compatible_vehicles.map((vehicle, idx) => (
                      <Badge key={idx} variant="secondary" className="gap-1 py-1">
                        {vehicle}
                        <button type="button" onClick={() => handleRemoveVehicle(idx)}>
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Fiyat & Stok</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="price">Fiyat (TL) *</Label>
                  <Input
                    id="price"
                    name="price"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.price}
                    onChange={handleChange}
                    className="mt-1"
                    data-testid="product-price-input"
                  />
                </div>

                <div>
                  <Label htmlFor="stock">Stok Adedi *</Label>
                  <Input
                    id="stock"
                    name="stock"
                    type="number"
                    placeholder="0"
                    value={formData.stock}
                    onChange={handleChange}
                    className="mt-1"
                    data-testid="product-stock-input"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Kategori & Durum</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Kategori *</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                  >
                    <SelectTrigger className="mt-1" data-testid="category-select">
                      <SelectValue placeholder="Kategori seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Ürün Durumu</Label>
                  <Select
                    value={formData.condition}
                    onValueChange={(value) => setFormData({ ...formData, condition: value })}
                  >
                    <SelectTrigger className="mt-1" data-testid="condition-select">
                      <SelectValue placeholder="Durum seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">Sıfır</SelectItem>
                      <SelectItem value="used">2. El</SelectItem>
                      <SelectItem value="refurbished">Yenilenmiş</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="space-y-3">
              <Button
                type="submit"
                className="w-full bg-orange-500 hover:bg-orange-600 h-12"
                disabled={loading}
                data-testid="save-product-btn"
              >
                {loading ? 'Kaydediliyor...' : isEdit ? 'Güncelle' : 'Ürünü Kaydet'}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => navigate('/seller/products')}
              >
                İptal
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;
